import {
  createContext,
  use,
  useState,
  useCallback,
  type ReactNode,
} from "react";

import { UserContext } from "./UserContext";

export type AssumedRole =
  | "clinical-operations"
  | "data-management"
  | "safety-medical-monitoring"
  | "risk-quality"
  | "clinical-development"
  | "clinical-science";

export interface RoleOption {
  key: AssumedRole;
  label: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- context file intentionally exports both provider and hook/constants
export const ROLE_OPTIONS: RoleOption[] = [
  { key: "clinical-operations", label: "Clinical Operations" },
  { key: "data-management", label: "Data Management" },
  { key: "safety-medical-monitoring", label: "Safety/Medical Monitoring" },
  { key: "risk-quality", label: "Risk & Quality" },
  { key: "clinical-development", label: "Clinical Development" },
  { key: "clinical-science", label: "Clinical Science" },
];

const STORAGE_KEY = "vivo_assumed_role";

interface AssumedRoleContextValue {
  assumedRole: AssumedRole | null;
  setAssumedRole: (role: AssumedRole | null) => void;
  assumedRoleLabel: string | null;
}

const AssumedRoleContext = createContext<AssumedRoleContextValue>({
  assumedRole: null,
  setAssumedRole: () => {},
  assumedRoleLabel: null,
});

export function AssumedRoleProvider({ children }: { children: ReactNode }) {
  const [assumedRole, setAssumedRole] = useState<AssumedRole | null>(
    () => (localStorage.getItem(STORAGE_KEY) as AssumedRole | null) ?? null,
  );

  const selectAssumedRole = useCallback((role: AssumedRole | null) => {
    if (role === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, role);
    }
    setAssumedRole(role);
  }, []);

  const assumedRoleLabel =
    ROLE_OPTIONS.find((r) => r.key === assumedRole)?.label ?? null;

  return (
    <AssumedRoleContext
      value={{
        assumedRole,
        setAssumedRole: selectAssumedRole,
        assumedRoleLabel,
      }}
    >
      {children}
    </AssumedRoleContext>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context file intentionally exports both provider and hook
export function useAssumedRole() {
  return use(AssumedRoleContext);
}

/**
 * The assumed role that should actually filter the UI. View As Role is a
 * superuser-only demo control, so for anyone else this is always null: a role
 * left in localStorage from before the control was restricted would otherwise
 * keep hiding their navigation with no menu item left to clear it.
 *
 * Reads UserContext optionally because this provider sits above UserProvider
 * and some screens using it are rendered in tests without one.
 */
// eslint-disable-next-line react-refresh/only-export-components -- context file intentionally exports both provider and hook
export function useEffectiveAssumedRole(): AssumedRole | null {
  const { assumedRole } = use(AssumedRoleContext);
  const isSuperuser = Boolean(use(UserContext)?.userInfo?.is_superuser);
  return isSuperuser ? assumedRole : null;
}
